import { useMemo } from "react";
import { encode } from "uqr";

// A QR code rendered as inline SVG from the module matrix of `uqr`
// (unjs/uqr: MIT, zero dependencies, a port of Project Nayuki's reference
// QR generator). The SVG is built as React elements — no innerHTML — and
// always dark-on-light with a 4-module quiet zone, whatever the theme,
// because that is what phone scanners read reliably. Error correction is
// "M" (15%), which survives screen glare and moiré at no real size cost
// for an otpauth:// URI.

export const QR_QUIET_ZONE = 4;

/** The boolean module matrix (true = dark), including the quiet zone. Exported for tests. */
export function qrMatrix(text: string): boolean[][] {
  return encode(text, { ecc: "M", border: QR_QUIET_ZONE }).data;
}

/** One SVG path covering every dark module, merging horizontal runs. */
function pathFor(matrix: boolean[][]): string {
  let d = "";
  matrix.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      if (!row[x]) {
        x++;
        continue;
      }
      const start = x;
      while (x < row.length && row[x]) x++;
      d += `M${start} ${y}h${x - start}v1h${start - x}z`;
    }
  });
  return d;
}

export function QrCode({ value, label, size = 200 }: { value: string; label: string; size?: number }) {
  const { n, d } = useMemo(() => {
    const m = qrMatrix(value);
    return { n: m.length, d: pathFor(m) };
  }, [value]);
  return (
    <svg
      className="qr-code"
      role="img"
      aria-label={label}
      viewBox={`0 0 ${n} ${n}`}
      width={size}
      height={size}
      shapeRendering="crispEdges"
    >
      <rect width={n} height={n} fill="#fff" />
      <path d={d} fill="#000" />
    </svg>
  );
}
